import React, { useEffect, useState } from 'react'

import { usePet } from '../../modules'
import PreauthCheck from '../modals/preauthCheck'

const appearance = {
    approved: 'bg-green-100 text-green-700 border-green-200',
    pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    rejected: 'bg-red-100 text-red-700 border-red-200',
    expired: 'bg-gray-100 text-gray-700 border-gray-200',
}

const PreauthStatusCard = ({ admissionId, orderId }) => {
    const [preauths, setPreauths] = useState([])
    const [claim, setClaim] = useState(null)
    const [selectedMissingItem, setSelectedMissingItem] = useState(null)
    const scopeLabel = admissionId ? 'admission' : orderId ? 'bill' : 'visit'
    const scopePayload = {
        admission: admissionId,
        order: orderId,
        limit: 50,
    }
    const claimPayload = {
        admission: admissionId,
        order: orderId,
        ensure: true,
    }

    const [{ loading, error }, fetchPreauths] = usePet('searchPreauths', {
        admission: admissionId,
        order: orderId,
        limit: 50,
    }, {
        notify: false,
        manual: true,
        callback: ({ data }) => setPreauths(data?.preauths ?? []),
    })

    const [{ loading: claimLoading }, fetchClaim] = usePet('claimStatus', {
        admission: admissionId,
        order: orderId,
        ensure: true,
    }, {
        notify: false,
        manual: true,
        callback: ({ data }) => setClaim(data),
    })

    const [{ loading: attaching }, attachPreauthToLineItem] = usePet('attachPreauthToLineItem', null, {
        notify: true,
        manual: true,
        callback: () => {
            setSelectedMissingItem(null)
            fetchPreauths(scopePayload)
            fetchClaim(claimPayload)
        },
        load: { msg: 'Attaching preauthorization...' }
    })

    const [{ loading: refreshing }, refreshPreauthStatus] = usePet('refreshPreauthStatus', null, {
        notify: true,
        manual: true,
        callback: () => {
            fetchPreauths(scopePayload)
            fetchClaim(claimPayload)
        },
        load: { msg: 'Refreshing SHA preauthorization status...' }
    })

    const [{ loading: resending }, resendPreauthDoctorConsent] = usePet('resendPreauthDoctorConsent', null, {
        notify: true,
        manual: true,
        callback: () => fetchPreauths(scopePayload),
        load: { msg: 'Requesting SHA doctor consent...' }
    })

    useEffect(() => {
        if (!admissionId && !orderId) return

        fetchPreauths(scopePayload)
        fetchClaim(claimPayload)
        const intervalId = setInterval(() => fetchPreauths(scopePayload), 45000)
        const claimIntervalId = setInterval(() => fetchClaim(claimPayload), 45000)

        return () => {
            clearInterval(intervalId)
            clearInterval(claimIntervalId)
        }
    }, [admissionId, orderId]) // eslint-disable-line react-hooks/exhaustive-deps

    if (!admissionId && !orderId) return null

    if (loading && preauths.length === 0) {
        return (
            <div className="shadow-lg rounded-sm border mb-3 border-gray-200 px-4 py-4">
                <p className="font-medium text-sm mb-1">Pre-Authorizations</p>
                <p className="text-sm text-gray-500 mb-0">Checking preauthorization requests...</p>
            </div>
        )
    }

    if (error && preauths.length === 0) {
        return (
            <div className="shadow-lg rounded-sm border mb-3 border-red-200 bg-red-50 px-4 py-4">
                <p className="font-medium text-sm mb-1 text-red-700">Pre-Authorizations</p>
                <p className="text-sm text-red-600 mb-0">Unable to load preauthorization status right now.</p>
            </div>
        )
    }

    if (preauths.length === 0) {
        return (
            <div className="shadow-lg rounded-sm border mb-3 border-gray-200 px-4 py-4">
                <p className="font-medium text-sm mb-1">Pre-Authorizations</p>
                <p className="text-sm text-gray-500 mb-0">No preauthorizations have been recorded for this {scopeLabel} yet.</p>
            </div>
        )
    }

    const missingItems = claim?.preauthSummary?.missing ?? []

    return (
        <div className="shadow-lg rounded-sm border mb-3 border-gray-200 px-4 pt-3 pb-4">
            <div className="flex items-center justify-between border-b-2 border-gray-200 w-full pb-2">
                <p className="font-medium text-sm mb-0">Pre-Authorizations</p>
            </div>
            <div className="pt-3 space-y-3">
                {
                    selectedMissingItem &&
                    <PreauthCheck
                        visible={ !!selectedMissingItem }
                        toggle={ () => setSelectedMissingItem(null) }
                        patient={ claim?.patientData }
                        saleItem={{
                            lineItem: selectedMissingItem?.lineItem,
                            name: selectedMissingItem?.name,
                            preauthCategory: selectedMissingItem?.category,
                            price: selectedMissingItem?.estimatedAmount,
                        }}
                        onCreated={ () => {
                            setSelectedMissingItem(null)
                            fetchPreauths(scopePayload)
                            fetchClaim(claimPayload)
                        } }
                        onProceed={preauth => attachPreauthToLineItem({
                            lineItem: selectedMissingItem?.lineItem,
                            preauth: preauth?._id ?? preauth,
                        })} />
                }
                {
                    missingItems.length > 0 &&
                    <div className="rounded-sm border border-yellow-200 bg-yellow-50 px-3 py-3">
                        <p className="text-xs font-semibold text-yellow-800 mb-2">Missing preauthorizations</p>
                        {
                            missingItems.map(item =>
                                <div key={ item.lineItem } className="flex items-center justify-between gap-3 mb-2 last:mb-0">
                                    <div>
                                        <p className="text-sm text-yellow-800 mb-0">{item.name} ({item.category})</p>
                                        <p className="text-xs text-yellow-700 mb-0">Estimated Amount: Ksh. {item.estimatedAmount ?? 0}</p>
                                    </div>
                                    <button
                                        disabled={attaching || claimLoading}
                                        onClick={() => setSelectedMissingItem(item)}
                                        className="btn hisani-btn-orange text-white">
                                        Add Preauth
                                    </button>
                                </div>
                            )
                        }
                    </div>
                }
                {
                    preauths.map(preauth =>
                        <div key={ preauth?._id } className="rounded-sm border border-gray-200 px-3 py-3">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-sm font-medium mb-1">{preauth?.procedureName}</p>
                                    <p className="text-xs text-gray-500 mb-1">
                                        {preauth?.category} • {preauth?.referenceNumber}
                                    </p>
                                    <p className="text-xs text-gray-500 mb-0">
                                        Estimated: Ksh. {preauth?.estimatedAmount ?? 0}
                                        {preauth?.approvedAmount ? ` • Approved: Ksh. ${preauth.approvedAmount}` : ''}
                                    </p>
                                    <p className="text-xs text-gray-500 mb-0">
                                        SHA Status: {preauth?.shaStatus ?? 'Pending'}
                                        {preauth?.isElective ? ' • Elective / doctor approval required' : ''}
                                    </p>
                                </div>
                                <span className={`inline-flex font-medium rounded-full border px-2.5 py-0.5 text-xs ${appearance[preauth?.status] ?? appearance.pending}`}>
                                    {preauth?.status ?? 'pending'}
                                </span>
                            </div>

                            {
                                preauth?.rejectionReason &&
                                <p className="text-sm text-red-700 mt-2 mb-0">{preauth.rejectionReason}</p>
                            }

                            {
                                <div className="mt-3 flex flex-wrap justify-end gap-2">
                                    <button
                                        disabled={refreshing}
                                        onClick={() => refreshPreauthStatus({ id: preauth?._id })}
                                        className="btn bg-white border border-gray-300 hover:bg-gray-50">
                                        Refresh SHA Status
                                    </button>
                                    {
                                        preauth?.isElective &&
                                        preauth?.shaStatus === 'PENDING_DOCTOR_APPROVAL' &&
                                        <button
                                            disabled={resending}
                                            onClick={() => resendPreauthDoctorConsent({ id: preauth?._id })}
                                            className="btn bg-white border border-gray-300 hover:bg-gray-50">
                                            Resend Doctor Consent
                                        </button>
                                    }
                                </div>
                            }
                        </div>
                    )
                }
            </div>
        </div>
    )
}

export default PreauthStatusCard
